import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { PRIORITY_LABEL, STATUS_LABEL, CATEGORY_LABEL, slaCountdown } from "../lib/labels";
import { Warning, Clock, FireSimple, Timer, Phone } from "@phosphor-icons/react";

function elapsedSince(iso, now) {
  if (!iso) return "—";
  const d = new Date(iso).getTime();
  const diff = Math.max(0, now - d) / 1000;
  const days = Math.floor(diff / 86400);
  const hours = Math.floor((diff % 86400) / 3600);
  const mins = Math.floor((diff % 3600) / 60);
  const secs = Math.floor(diff % 60);
  if (days > 0) return `${days}j ${hours.toString().padStart(2, "0")}h ${mins.toString().padStart(2, "0")}m`;
  return `${hours.toString().padStart(2, "0")}h ${mins.toString().padStart(2, "0")}m ${secs.toString().padStart(2, "0")}s`;
}

function stripHtml(s) {
  if (!s) return "";
  return s.replace(/<[^>]+>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();
}

export default function WallView() {
  const [items, setItems] = useState([]);
  const [now, setNow] = useState(Date.now());
  const [searchParams] = useSearchParams();
  const publicToken = searchParams.get("token");
  // v261 — Force TV layout regardless of the actual viewport width (useful
  // for Android TV players / rugged tablets that report low CSS widths and
  // would otherwise show the mobile 1-column layout with oversized cards).
  // Usage: append `?layout=tv` (or `?layout=tv&token=...`) to the URL.
  const forceTv = (searchParams.get("layout") || "").toLowerCase() === "tv";

  const load = async () => {
    try {
      const url = publicToken ? `/wall/public?token=${encodeURIComponent(publicToken)}` : "/stats/urgents";
      const r = await api.get(url);
      setItems(r.data);
    } catch { /* noop */ }
  };

  useEffect(() => {
    document.body.classList.add("dark");
    load();
    const id1 = setInterval(load, 20000);
    const id2 = setInterval(() => setNow(Date.now()), 1000);
    return () => { clearInterval(id1); clearInterval(id2); document.body.classList.remove("dark"); };
  }, []);

  const critique = items.filter((t) => t.priority === "critique");
  const urgente = items.filter((t) => t.priority === "urgente");

  return (
    <div className={`wall p-4 sm:p-6 md:p-8 ${forceTv ? "wall-force-tv" : ""}`} data-testid="wall-root" data-force-tv={forceTv ? "1" : "0"}>
      <header className="flex items-start sm:items-center justify-between gap-4 mb-6 sm:mb-8 flex-wrap">
        <div className="min-w-0">
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.3em] text-white/50">MasterTicket · Mur des urgences</div>
          <h1 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mt-1 sm:mt-2 leading-tight">
            {critique.length > 0 && <span className="glow-critique text-red-400">{critique.length} CRITIQUE{critique.length > 1 ? "S" : ""}</span>}
            {critique.length > 0 && urgente.length > 0 && <span className="text-white/40 mx-2 sm:mx-3">·</span>}
            {urgente.length > 0 && <span className="text-orange-400">{urgente.length} URGENT{urgente.length > 1 ? "S" : ""}</span>}
            {items.length === 0 && <span className="text-emerald-400">RAS · Tout est sous contrôle</span>}
          </h1>
        </div>
        <div className="text-right shrink-0">
          <div className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mono leading-none">{new Date(now).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</div>
          <div className="text-[11px] sm:text-xs md:text-sm text-white/50 mt-1">{new Date(now).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}</div>
        </div>
      </header>

      {/* v261 — Grid breakpoint moved from lg (1024px) to sm (640px), so
          Android tablets/TV players (typically 800-1000px) get 2 columns
          instead of gigantic 1-column cards. Above 1280px we go to 3-col to
          exploit large TV screens. `.wall-force-tv` overrides via CSS to
          force 2-col even on narrow viewports (Android sticks). */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5 lg:gap-6">
        {items.map((t) => {
          const sla = slaCountdown(t.sla_resolution_deadline);
          const isCrit = t.priority === "critique";
          const openFor = elapsedSince(t.created_at, now);
          const desc = stripHtml(t.description);
          return (
            <div key={t.id}
              className={`rounded-xl p-4 sm:p-5 lg:p-6 border ${isCrit ? "bg-red-950/60 border-red-500/50 pulse-crit" : "bg-orange-950/40 border-orange-500/40"}`}
              data-testid={`wall-item-${t.number}`}
            >
              <div className="flex items-start justify-between mb-3 gap-3 sm:gap-4">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                  {isCrit
                    ? <FireSimple weight="fill" size={24} className="text-red-400 shrink-0"/>
                    : <Warning weight="fill" size={24} className="text-orange-400 shrink-0"/>}
                  <div className="min-w-0">
                    <div className="text-[10px] sm:text-xs uppercase tracking-widest text-white/60 truncate">{PRIORITY_LABEL[t.priority]} · {CATEGORY_LABEL[t.category]}</div>
                    <div className="mono text-[10px] sm:text-xs text-white/50">#{t.number} · {STATUS_LABEL[t.status]}</div>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[9px] sm:text-[10px] uppercase tracking-widest text-white/40 flex items-center justify-end gap-1">
                    <Timer size={10}/> Ouvert depuis
                  </div>
                  <div className="text-lg sm:text-xl lg:text-2xl font-bold mono text-amber-300 leading-tight" data-testid={`wall-elapsed-${t.number}`}>
                    {openFor}
                  </div>
                  <div className="text-[9px] sm:text-[10px] uppercase tracking-widest text-white/40 mt-1 flex items-center justify-end gap-1">
                    <Clock size={10}/> SLA
                  </div>
                  <div className={`text-base sm:text-lg lg:text-xl font-bold mono leading-tight ${sla.breached ? "text-red-400" : "text-white"}`}>
                    {sla.text}
                  </div>
                </div>
              </div>

              <h3 className="font-display text-lg sm:text-xl lg:text-2xl font-semibold text-white leading-snug">{t.subject}</h3>

              {desc && (
                <p className="text-xs sm:text-sm text-white/70 mt-2 line-clamp-2" data-testid={`wall-desc-${t.number}`}>
                  {desc}
                </p>
              )}

              <div className="mt-3 pt-3 border-t border-white/10 flex flex-wrap items-center gap-x-3 sm:gap-x-4 gap-y-1 text-xs sm:text-sm">
                <div className="text-white font-medium" data-testid={`wall-client-${t.number}`}>
                  {t.client_name}
                </div>
                {t.client_phone ? (
                  <a href={`tel:${(t.client_phone || "").replace(/\s+/g, "")}`}
                     className="inline-flex items-center gap-1 text-emerald-300 hover:underline mono"
                     data-testid={`wall-phone-${t.number}`}>
                    <Phone weight="fill" size={12}/>{t.client_phone}
                  </a>
                ) : (
                  <span className="text-white/40 text-[10px] sm:text-xs italic">téléphone non renseigné</span>
                )}
                <div className="ml-auto text-white/50 text-[10px] sm:text-xs">
                  Assigné : <span className="text-white/80">{t.assignee_name || "à faire"}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
